"use client";

import { useEffect, useRef } from 'react';
import { useStore } from '@/context/StoreContext';
import { getSubdomain } from '@/lib/subdomain';
import { getApiBaseUrl } from '@/lib/api';

declare global {
  interface Window {
    fbq?: any;
    _fbq?: any;
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

export default function MarketingPixels() {
  const { settings } = useStore();
  const initializedPixels = useRef<Set<string>>(new Set());

  useEffect(() => {
    const pixels = settings?.marketing_pixels;
    if (!pixels || !Array.isArray(pixels) || pixels.length === 0) return;

    const subdomain = getSubdomain();
    const apiBaseUrl = getApiBaseUrl();

    pixels.forEach((pixel) => {
      const pixelKey = `${pixel.provider}:${pixel.pixel_code}`;
      if (initializedPixels.current.has(pixelKey)) return;
      initializedPixels.current.add(pixelKey);

      try {
        // 1. META ADS / FACEBOOK PIXEL
        if (pixel.provider === 'facebook' && pixel.pixel_code) {
          if (!window.fbq) {
            const fbq: any = function () {
              fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
            };
            window.fbq = fbq;
            if (!window._fbq) window._fbq = fbq;
            fbq.push = fbq;
            fbq.loaded = true;
            fbq.version = '2.0';
            fbq.queue = [];

            const script = document.createElement('script');
            script.async = true;
            script.src = 'https://connect.facebook.net/en_US/fbevents.js';
            document.head.appendChild(script);
          }

          window.fbq('init', pixel.pixel_code);
          window.fbq('track', 'PageView');
        }

        // 2. GOOGLE ANALYTICS 4 (GA4)
        if (pixel.provider === 'google_analytics' && pixel.pixel_code) {
          if (!window.dataLayer) {
            window.dataLayer = [];
          }
          if (!window.gtag) {
            window.gtag = function () {
              window.dataLayer?.push(arguments);
            };
            window.gtag('js', new Date());

            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(pixel.pixel_code)}`;
            document.head.appendChild(script);
          }

          window.gtag('config', pixel.pixel_code, {
            send_page_view: true,
          });
        }

        // 3. GOOGLE ADS
        if (pixel.provider === 'google_ads' && pixel.pixel_code) {
          if (!window.dataLayer) {
            window.dataLayer = [];
          }
          if (!window.gtag) {
            window.gtag = function () {
              window.dataLayer?.push(arguments);
            };
            window.gtag('js', new Date());

            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(pixel.pixel_code)}`;
            document.head.appendChild(script);
          }

          window.gtag('config', pixel.pixel_code);
        }

        // 4. REGISTRO DE TELEMETRIA NO BACKEND DO ERP (marketing_pixel_events)
        if (subdomain) {
          fetch(`${apiBaseUrl}/public/pixel/track`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Tenant-Subdomain': subdomain,
            },
            body: JSON.stringify({
              pixel_id: pixel.id,
              event_type: 'PageView',
              subdomain: subdomain,
              tenant_id: subdomain,
            }),
          }).catch(() => {
            // Silencioso em caso de erro de rede
          });
        }
      } catch (err) {
        console.warn(`[MarketingPixels] Erro ao inicializar ${pixel.name}:`, err);
      }
    });
  }, [settings?.marketing_pixels]);

  return null;
}

